import type { Ref } from 'react';
import React, { useRef } from 'react';
import { IconCross } from '@/app/components/hotel-detail/common/icons';
import { IconArrowDownSolid } from '@/app/common/icons/libraryIcons';
import styled, { css } from 'styled-components';
import { focusRing } from '@/app/components/search/styles/mixins';
import { getBrandColorUsingCssFilterOnImg } from '@/utils/cssColorFilters.ts';
import type { Brand } from '@/domainLogic/index.ts';
import type { EvoFilterModalProps } from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/EvoFilterModal';
import { EvoFilterModal } from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/EvoFilterModal';

// #region EvoTransportPill Styles

const upDownArrow = css`
  picture {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    transition: transform 0.2s ease-in-out;
  }
`;

const PillContainer = styled.button<{
  $open?: boolean;
  $selected?: boolean;
  $hasArrow?: boolean;
  $brand?: Brand;
  $shape?: EvoTransportPillShape;
  $disabled?: boolean;
}>`
  height: 40px;
  background-color: #fff;
  /* MPH-1050: iOS Safari paints a <button> with its own system blue instead of inheriting the
     page text colour, so the label and the chevron (which is filled with currentColor) came out
     blue on iPhone. Set the resting colour explicitly; the selected, open, hover and disabled
     states below still override it. */
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  box-shadow: 0 0 0 1px ${({ theme }) => theme.kallisto.color.common.black[100]};
  border-radius: ${({ $shape, theme }) => ($shape === 'rectangular' ? theme.kallisto.border.radius.radius100 : '19px')};
  font-size: ${({ theme }) => theme.kallisto.font.size['175']};
  line-height: ${({ theme }) => theme.kallisto.font.lineHeight['250']};
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px;
  position: relative;
  white-space: nowrap;
  scroll-snap-align: center;
  border: 0;

  span {
    padding: 0px 8px;
  }

  /* Both labels share one grid cell, so the pill is as wide as the longer one whichever is shown */
  .evo-pill-label {
    display: inline-grid;
  }

  .evo-pill-label > span {
    grid-area: 1 / 1;
    padding: 0;
  }

  .evo-pill-label > span[aria-hidden='true'] {
    visibility: hidden;
  }

  ${({ $hasArrow }) => $hasArrow && upDownArrow};

  ${({ $selected, $brand }) =>
    $selected &&
    $brand &&
    css`
      background-color: ${({ theme }) => theme.kallisto.color.background.interactive.backgroundPrimarySelected};
      box-shadow: 0 0 0 1.5px ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
      color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
      ${getBrandColorUsingCssFilterOnImg($brand)}
    `}

  &:focus-visible {
    ${focusRing}
  }

  &:hover {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
    ${({ $selected, $brand }) =>
      !$selected &&
      $brand &&
      css`
        color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
        ${getBrandColorUsingCssFilterOnImg($brand)}
      `}
  }

  ${({ $open, $brand, $hasArrow }) =>
    $open &&
    $brand &&
    css`
      box-shadow: 0 0 0 1.5px ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
      color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
      ${getBrandColorUsingCssFilterOnImg($brand)}
      ${
        $hasArrow &&
        css`
          picture {
            transform: rotate(180deg);
          }
        `
      }
    `}

  /* MPH-469: non-interactive pill while alternative flights load, per Figma disabled tokens.
     The Sort field (rectangular) keeps a white background + border; the filter pills get the
     solid grey disabled fill. Both grey their content (text, icons, chevron). */
  ${({ $disabled, $shape }) =>
    $disabled &&
    css`
      background-color: ${({ theme }) =>
        $shape === 'rectangular'
          ? theme.kallisto.color.background.static.backgroundLayer02
          : theme.kallisto.color.background.static.backgroundDisabled};
      box-shadow: 0 0 0 1px ${({ theme }) => theme.kallisto.color.border.static.borderDisabled};
      color: ${({ theme }) => theme.kallisto.color.text.static.textDisabled};
      pointer-events: none;
      cursor: default;

      svg,
      [data-library-icon] {
        color: ${({ theme }) => theme.kallisto.color.icon.static.iconDisabled};
      }

      &:hover {
        box-shadow: 0 0 0 1px ${({ theme }) => theme.kallisto.color.border.static.borderDisabled};
        color: ${({ theme }) => theme.kallisto.color.text.static.textDisabled};
      }
    `}
`;

const ClearIcon = styled.div`
  display: flex;
  justify-content: center;
  cursor: pointer;
  font-weight: bold;
  color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
`;

// #endregion EvoTransportPill Styles

export type EvoTransportPillShape = 'pill' | 'rectangular';

export type EvoTransportPillProps = {
  label: string;
  // Label of another state of the same pill: its width is reserved so the pill does not resize when the label changes
  reservedLabel?: string;
  hasArrow: boolean;
  control: string;
  iconLeft?: React.ReactNode;
  selected?: boolean;
  onPillClick?: (filterId: string) => void;
  onCrossClick?: (filterId: string) => void;
  isActive?: boolean;
  brand: Brand;
  isOpen: boolean;
  modal?: EvoFilterModalProps;
  shape?: EvoTransportPillShape;
  style?: React.CSSProperties;
  // MPH-469: greyed-out, non-interactive pill while alternative flights are loading
  disabled?: boolean;
};

export function EvoTransportPill(props: EvoTransportPillProps) {
  const {
    label,
    reservedLabel,
    hasArrow,
    control,
    iconLeft,
    selected,
    onPillClick,
    onCrossClick,
    isActive,
    brand,
    isOpen,
    modal,
    shape,
    style,
    disabled,
  } = props;
  const containerRef = useRef<HTMLButtonElement>(undefined);

  const handleOnClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onPillClick && control) {
      onPillClick(control);
    }
  };

  const handleClearClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onCrossClick && control) {
      onCrossClick(control);
    }
  };

  return (
    <>
      <PillContainer
        ref={containerRef as Ref<HTMLButtonElement>}
        onClick={handleOnClick}
        id={`Pill-${control}`}
        className={`Pill-${control}`}
        type="button"
        $selected={selected}
        $hasArrow={hasArrow}
        $brand={brand}
        $open={isOpen}
        $shape={shape}
        $disabled={disabled}
        aria-disabled={disabled || undefined}
        // MPH-469: keep the greyed-out loading pills out of the keyboard tab order
        tabIndex={disabled ? -1 : undefined}
        style={style}
      >
        {iconLeft}
        {reservedLabel ? (
          <span className="evo-pill-label">
            <span>{label}</span>
            <span aria-hidden="true">{reservedLabel}</span>
          </span>
        ) : (
          <span>{label}</span>
        )}
        {hasArrow && (
          <picture>
            <IconArrowDownSolid size={16} />
          </picture>
        )}
        {isActive && !hasArrow && (
          <ClearIcon onClick={handleClearClick}>
            <IconCross size={20} />
          </ClearIcon>
        )}
      </PillContainer>
      {modal && <EvoFilterModal {...modal} containerRef={containerRef} />}
    </>
  );
}
